/*
 * PriorityIcon — colour-coded glyph for task priority.
 *
 * A tinted square frame holding a react-icon:
 *   Urgent  → TbUrgent  (red)
 *   High    → BiSignal5 (orange, all bars filled)
 *   Medium  → BiSignal3 (amber, three bars filled)
 *   Low     → BiSignal2 (blue,  two bars filled)
 *   None    → BsSlashCircle (slate, dimmed)
 */

import React from 'react';
import { TbUrgent } from 'react-icons/tb';
import { BiSignal2, BiSignal3, BiSignal5 } from 'react-icons/bi';
import { BsSlashCircle } from 'react-icons/bs';

const TONE = {
  Urgent: { border: 'var(--state-error)', bg: 'var(--state-error-bg)', fg: 'var(--state-error)' },
  High: {
    border: 'var(--brand-primary)',
    bg: 'rgba(238, 115, 27, 0.12)',
    fg: 'var(--brand-primary)'
  },
  Medium: {
    border: 'var(--state-warning)',
    bg: 'var(--state-warning-bg)',
    fg: 'var(--state-warning)'
  },
  Low: { border: 'var(--state-info)', bg: 'var(--state-info-bg)', fg: 'var(--state-info)' },
  None: { border: 'var(--border-default)', bg: 'var(--surface-sunken)', fg: 'var(--text-muted)' }
};

const ICON = {
  Urgent: TbUrgent,
  High: BiSignal5,
  Medium: BiSignal3,
  Low: BiSignal2,
  None: BsSlashCircle
};

function PriorityIcon({ priority = 'None', size = 18, bare = false }) {
  const tone = TONE[priority] || TONE.None;
  const Icon = ICON[priority] || ICON.None;

  if (bare) {
    return <Icon size={size} color={tone.fg} aria-label={`${priority} priority`} />;
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: 4,
        border: `1px solid ${tone.border}`,
        background: tone.bg,
        flex: 'none'
      }}
      aria-label={`${priority} priority`}
    >
      <Icon size={size - 6} color={tone.fg} />
    </span>
  );
}

export default PriorityIcon;
